import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Button,
} from '@mui/material';
import {
  BookmarkBorder,
  Favorite,
  LocalOffer,
  Star,
  Add,
  ViewList,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { sourcesApi } from '../services/api';
import { Stats } from '../types';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await sourcesApi.getStats();
        setStats(data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h4" component="h1" gutterBottom>
          Loading Dashboard...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Welcome to Useful Sources
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Your personal knowledge repository
        </Typography>
      </Box>

      {/* My Statistics Section */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
          📊 My Statistics
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BookmarkBorder sx={{ mr: 1 }} />
                <Typography variant="body2">My Sources</Typography>
              </Box>
              <Typography variant="h3">
                {stats?.userStats?.totalSources || 0}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Favorite sx={{ mr: 1 }} />
                <Typography variant="body2">My Favorites</Typography>
              </Box>
              <Typography variant="h3">
                {stats?.userStats?.favoriteSources || 0}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocalOffer sx={{ mr: 1 }} />
                <Typography variant="body2">My Tags</Typography>
              </Box>
              <Typography variant="h3">
                {stats?.userStats?.myTags || 0}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Star sx={{ mr: 1 }} />
                <Typography variant="body2">My Avg Rating</Typography>
              </Box>
              <Typography variant="h3">
                {stats?.userStats?.averageRating || 0}★
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Library Statistics Section */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
          📚 Library Statistics (All Users)
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BookmarkBorder sx={{ mr: 1 }} />
                <Typography variant="body2">Total Sources</Typography>
              </Box>
              <Typography variant="h3">
                {stats?.appStats?.totalSources || 0}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Favorite sx={{ mr: 1 }} />
                <Typography variant="body2">Total Favorites</Typography>
              </Box>
              <Typography variant="h3">
                {stats?.appStats?.favoriteSources || 0}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocalOffer sx={{ mr: 1 }} />
                <Typography variant="body2">Total Tags</Typography>
              </Box>
              <Typography variant="h3">
                {stats?.appStats?.totalTags || 0}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Star sx={{ mr: 1 }} />
                <Typography variant="body2">Library Avg Rating</Typography>
              </Box>
              <Typography variant="h3">
                {stats?.appStats?.averageRating || 0}★
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/sources/add')}
            >
              Add New Source
            </Button>
            <Button
              variant="outlined"
              startIcon={<ViewList />}
              onClick={() => navigate('/sources')}
            >
              Browse All Sources
            </Button>
            <Button
              variant="outlined"
              startIcon={<Favorite />}
              onClick={() => navigate('/sources?favorites=true')}
            >
              View Favorites
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/sources?mySourcesFilter=public')}
            >
              View My Public
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/sources?mySourcesFilter=private')}
            >
              View My Private
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Dashboard;